import { z } from 'zod';
import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  AssignExternalNetworkScanProfileSchema,
  CreateScanProfileSchema,
  ScanProfileEntitySchema,
  ScanProfilePathParamsSchema,
  UpdateScanProfileSchema,
} from './scan-profile.schema';

export const scanProfileCatalogRoutes = {
  listScanProfiles: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/scan-profiles',
      summary: 'List scan profiles',
      description:
        'Lists all scan profiles for the tenant, seeding system profiles when missing.',
      inputStructure: 'detailed',
    })
    .output(z.array(ScanProfileEntitySchema)),
  createScanProfile: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/scan-profiles',
      summary: 'Create custom scan profile',
      inputStructure: 'detailed',
      successStatus: 201,
    })
    .input(z.object({ body: CreateScanProfileSchema }))
    .output(ScanProfileEntitySchema),
  getScanProfile: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/scan-profiles/{scanProfileId}',
      summary: 'Get scan profile',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: ScanProfilePathParamsSchema }))
    .output(ScanProfileEntitySchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
    }),
  updateScanProfile: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'PATCH',
      path: '/scan-profiles/{scanProfileId}',
      summary: 'Update scan profile',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: ScanProfilePathParamsSchema,
        body: UpdateScanProfileSchema,
      }),
    )
    .output(ScanProfileEntitySchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
    }),
  deleteScanProfile: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'DELETE',
      path: '/scan-profiles/{scanProfileId}',
      summary: 'Delete custom scan profile',
      inputStructure: 'detailed',
    })
    .input(
      z.object({ params: ScanProfilePathParamsSchema, body: z.object({}) }),
    )
    .output(z.object({ success: z.literal(true) }))
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
    }),
  resetScanProfile: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'POST',
      path: '/scan-profiles/{scanProfileId}/reset',
      summary: 'Reset system scan profile to factory settings',
      inputStructure: 'detailed',
    })
    .input(
      z.object({ params: ScanProfilePathParamsSchema, body: z.object({}) }),
    )
    .output(ScanProfileEntitySchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
    }),
  getExternalNetworkScanProfile: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
    })
    .route({
      method: 'GET',
      path: '/{surfaceId}/scan-profile',
      summary: 'Get assigned scan profile for surface',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: z.object({ surfaceId: z.string() }) }))
    .output(ScanProfileEntitySchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
    }),
  assignExternalNetworkScanProfile: terraContract
    .meta({
      permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
    })
    .route({
      method: 'PUT',
      path: '/{surfaceId}/scan-profile',
      summary: 'Assign scan profile to surface',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ surfaceId: z.string() }),
        body: AssignExternalNetworkScanProfileSchema,
      }),
    )
    .output(ScanProfileEntitySchema)
    .errors({
      NOT_FOUND: { status: 404, data: z.object({ message: z.string() }) },
      CONFLICT: { status: 409, data: z.object({ message: z.string() }) },
    }),
};
